import React, { useContext, useState, useEffect } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper,
	Stack,
	TextField,
	RadioGroup,
	FormControlLabel,
	Radio,
	Alert,
	IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalContext } from "../../GlobalContext";
import { createForecastFile, updateForecastFile, listForecasts } from "../../api/forecast.client";
import ForecastStepTwo from "./ForecastStepTwo";
import { useTranslation } from "react-i18next";

const MONTH_RE = /^\d{4}-\d{2}$/;
function hasAnyDataInPreview(json) {
	if (!json || typeof json !== "object") return false;
	const months = Object.keys(json).filter((k) => MONTH_RE.test(k));
	for (const m of months) {
		const block = json[m] || {};
		const dayKeys = Object.keys(block).filter((k) => !["inicio", "fin", "start", "end"].includes(k));
		for (const dk of dayKeys) {
			const horas = block[dk];
			if (horas && typeof horas === "object" && Object.keys(horas).length > 0) {
				return true; // hay al menos una hora con valor
			}
		}
	}
	return false;
}

export default function ImportForecastModal({ open, onClose, onSaved }) {
	const { site_id } = useContext(GlobalContext);
	const { t } = useTranslation();
	const [step, setStep] = useState(0);
	const [values, setValues] = useState({
		start: "",
		end: "",
		forecast_origin: "DB",
		nombre: "",
	});
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [preview, setPreview] = useState(null); // respuesta de create
	const [createdId, setCreatedId] = useState(null); // ⬅️ ID del archivo creado
	const [saving, setSaving] = useState(false);
	const goStep = (i) => setStep(i);

	const resetModal = () => {
		setStep(0);
		setValues({ start: "", end: "", forecast_origin: "DB", nombre: "" });
		setBusy(false);
		setError(null);

		setPreview(null);
		setCreatedId(null);
		setSaving(false);
	};

	const handleCreate = async () => {
		setBusy(true);
		setError(null);
		setPreview(null);
		try {
			const res = await createForecastFile({ site_id, ...values });
			const candidate = res?.preview ?? res?.data?.preview ?? res ?? null;
			const msg = res?.message ?? res?.data?.message ?? candidate?.message;
			// 2) Sin días/horas en el rango => no avanzar
			if (!hasAnyDataInPreview(candidate)) {
				setError(
					values.forecast_origin === "KRONOS"
						? "No hay datos de demanda en KRONOS para las fechas seleccionadas."
						: "No hay datos de demanda para el rango seleccionado."
				);
				return;
			}

			setPreview(candidate);
			setCreatedId(null);
			goStep(1);
			if (msg && /no hay datos|no se pudo obtener/i.test(String(msg))) {
				setError(msg);
				return;
			}
		} catch (e) {
			setError(e?.response?.data?.message || e?.message || t("forecast.modal.errorCreate"));
		} finally {
			setBusy(false);
		}
	};

	const handleSaveStepTwo = async (payloadFromStepTwo) => {
		setError(null);
		try {
			setSaving(true);
			let fid = createdId ?? preview?.file_id ?? preview?.id ?? preview?.data?.file_id ?? null;

			if (!fid) {
				const items = await listForecasts(site_id);
				const rows = Array.isArray(items) ? items.slice() : [];
				// normalizar a YYYY-MM
				const sMonth = String(values.start || "").slice(0, 7);
				const eMonth = String(values.end || "").slice(0, 7);
				const norm = (s) => String(s || "").slice(0, 7);

				// ordena por id desc para “reciente primero”
				rows.sort((a, b) => String(b?.id).localeCompare(String(a?.id)));

				// tier 1: nombre + origen + start == values.start + end == values.end
				let hit = rows.find(
					(it) =>
						it?.nombre === values.nombre &&
						(it?.origen || it?.origin) === values.forecast_origin &&
						norm(it?.start_dtm) === sMonth &&
						+norm(it?.end_dtm) === eMonth
				);
				// tier 2: nombre + origen + start == values.start  (fin puede diferir)
				if (!hit)
					hit = rows.find(
						(it) =>
							it?.nombre === values.nombre &&
							(it?.origen || it?.origin) === values.forecast_origin &&
							norm(it?.start_dtm) === sMonth
					);
				// tier 3: nombre + origen (lo más común si solo cambió el rango)
				if (!hit)
					hit = rows.find(
						(it) => it?.nombre === values.nombre && (it?.origen || it?.origin) === values.forecast_origin
					);
				// tier 4: origen (último recurso: el más reciente del mismo origen)
				if (!hit) hit = rows.find((it) => (it?.origen || it?.origin) === values.forecast_origin);

				if (hit?.id != null) fid = hit.id;
			}
			if (!fid) {
				setError("Falta file_id para actualizar el forecast.");
				setSaving(false);
				return;
			}

			await updateForecastFile({ site_id, file_id: fid, body: payloadFromStepTwo });
			onSaved?.();
			onClose?.();
		} catch {
			setError("No se pudo guardar los cambios (update).");
		} finally {
			setSaving(false);
		}
	};
	const invalidRange = values.start && values.end && values.end < values.start;
	const isStepTwo = step === 1;
	const handleClose = () => {
		resetModal();
		onClose?.();
	};

	useEffect(() => {
		if (!open) resetModal();
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			container={typeof window !== "undefined" ? document.body : undefined}
			keepMounted
			fullScreen={false}
			fullWidth={isStepTwo}
			scroll="paper"
			maxWidth={isStepTwo ? false : "xs"}
			PaperProps={{
				sx: {
					width: { xs: "95vw", md: "min(1200px, 95vw)" }, // ancho del modal
					maxHeight: "90vh",
					display: "flex",
					flexDirection: "column",
				},
			}}
			slotProps={{
				backdrop: {
					sx: { backdropFilter: "blur(1px)" },
				},
			}}
		>
			<DialogTitle sx={{ pr: 6 }}>
				{isStepTwo
					? t("forecast.modal.importTitleStep2", "Revisar datos")
					: t("forecast.modal.importTitle", "Importar datos")}
				<IconButton onClick={handleClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					flex: 1,
					overflow: "auto",
					p: 2,
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
				}}
			>
				<Stepper activeStep={step} sx={{ mb: 3 }}>
					<Step>
						<StepLabel>{t("forecast.modal.step", { current: 1, total: 2 })}</StepLabel>
					</Step>
					<Step>
						<StepLabel>{t("forecast.modal.step", { current: 2, total: 2 })}</StepLabel>
					</Step>
				</Stepper>

				{step === 0 && (
					<Box
						sx={{
							alignSelf: "center",
							width: "23.125rem", // compacto
							maxWidth: "100%",
						}}
					>
						<Stack spacing={2} maxWidth={440}>
							<TextField
								type="month"
								label={t("forecast.form.startMonth")}
								value={values.start}
								onChange={(e) => setValues((v) => ({ ...v, start: e.target.value }))}
								InputLabelProps={{
									shrink: true,
								}}
							/>
							<TextField
								type="month"
								label={t("forecast.form.endMonth")}
								value={values.end}
								onChange={(e) => setValues((v) => ({ ...v, end: e.target.value }))}
								InputLabelProps={{
									shrink: true,
								}}
							/>
							<RadioGroup
								row
								value={values.forecast_origin}
								onChange={(e) => setValues((v) => ({ ...v, forecast_origin: e.target.value }))}
								aria-label={t("forecast.form.origin")}
							>
								<FormControlLabel value="KRONOS" control={<Radio />} label={t("forecast.form.originUKG")} />
								<FormControlLabel value="DB" control={<Radio />} label={t("forecast.form.originDB")} />
							</RadioGroup>
							<TextField
								label={t("forecast.form.name")}
								value={values.nombre}
								onChange={(e) => setValues((v) => ({ ...v, nombre: e.target.value }))}
								placeholder={t("forecast.form.namePlaceholder")}
								InputLabelProps={{
									shrink: true,
								}}
							/>

							{error && (
								<Alert severity="error" role="alert">
									{typeof error === "string" ? error : t("forecast.modal.errorCreate")}
								</Alert>
							)}

							<Stack direction="row" spacing={1}>
								<Button onClick={onClose}>{t("forecast.modal.cancel")}</Button>
								<Button
									variant="contained"
									disabled={busy || !values.start || !values.end || !values.nombre || invalidRange}
									onClick={handleCreate}
								>
									{busy ? t("forecast.modal.creating") : t("forecast.modal.continue")}
								</Button>
							</Stack>
						</Stack>
					</Box>
				)}

				{step === 1 && (
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							alignItems: "stretch",
						}}
					>
						{!!error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{typeof error === "string" ? error : t("forecast.modal.errorUpdate")}
							</Alert>
						)}

						<ForecastStepTwo
							data={preview}
							mode="create"
							onBack={() => goStep(0)}
							onSave={handleSaveStepTwo}
							sx={{ flex: 1, minHeight: 0 }}
							saving={saving}
						/>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
}
